import React, { useState, useEffect } from 'react';
import { Clock, ChevronLeft, ChevronRight, Info } from 'lucide-react';
import ServiceDetailModal from './ServiceDetailModal';
import BookingModal from './BookingModal';

// Import optimized WebP images for better performance
import massageImg from '../assets/optimized/massage-therapy.webp';

// Import optimized service images (WebP - 93% smaller)
import serviceSwedish from '../assets/optimized/service-swedish.webp';
import serviceDeepTissue from '../assets/optimized/service-deeptissue.webp';
import serviceThai from '../assets/optimized/service-thai.webp';
import serviceAyurvedic from '../assets/optimized/service-ayurvedic.webp';
import serviceShirodhara from '../assets/optimized/service-shirodhara.webp';
import serviceCouple from '../assets/optimized/service-couple.webp';
import serviceHeadNeck from '../assets/optimized/service-headneck.webp';
import serviceFootReflex from '../assets/optimized/service-footreflex.webp';
import serviceSports from '../assets/optimized/service-sports.webp';
import serviceBasti from '../assets/optimized/service-basti.webp';
import servicePotli from '../assets/optimized/service-potli.webp';
import serviceThaiNew from '../assets/optimized/service-thai-new.webp';
import serviceMarma from '../assets/optimized/service-marma.webp';
import serviceParalysis from '../assets/optimized/service-paralysis.webp';
import serviceCoupleNew from '../assets/optimized/service-couple-new.webp';
import serviceHeadNeckNew from '../assets/optimized/service-headneck-new.webp';
import serviceDeepTissueNew from '../assets/optimized/service-deeptissue-new.webp';
import serviceHairRemoval from '../assets/optimized/service-hairremoval.webp';
import serviceBabyMother from '../assets/optimized/service-babymother.webp';
import serviceSportsNew from '../assets/optimized/service-sports-new.webp';
import serviceBodyScrub from '../assets/optimized/service-bodyscrub.webp';
import serviceJanuBasti from '../assets/optimized/service-janu-basti.webp';
import serviceKatiBasti from '../assets/optimized/service-kati-basti.webp';
import serviceGreevaBasti from '../assets/optimized/service-greeva-basti.webp';
import serviceNadiSweda from '../assets/optimized/service-nadisweda.webp';
import serviceBodyPoint from '../assets/optimized/service-bodypoint.png';
import serviceNervousSystem from '../assets/optimized/service-nervous-system.png';
import service10Finger from '../assets/optimized/service-10finger.png';
import serviceSleep from '../assets/optimized/service-sleep.png';

// Import optimized equipment images
import equipmentTable2 from '../assets/optimized/equipment-table2.webp';
import equipmentOils from '../assets/optimized/equipment-oils.webp';

// Equipment Carousel Component
const EquipmentCarousel = () => {
    const [currentSlide, setCurrentSlide] = useState(0);

    const equipmentImages = [
        // { src: equipmentTable1, caption: 'Professional Massage Table Setup' },
        { src: equipmentTable2, caption: 'Complete Home Service Equipment' },
        { src: equipmentOils, caption: 'Premium Hot Oils & Herbal Pouches' },
    ];

    // Auto-slide every 4 seconds
    useEffect(() => {
        const timer = setInterval(() => {
            setCurrentSlide((prev) => (prev + 1) % equipmentImages.length);
        }, 4000);
        return () => clearInterval(timer);
    }, [equipmentImages.length]);

    const nextSlide = () => {
        setCurrentSlide((prev) => (prev + 1) % equipmentImages.length);
    };

    const prevSlide = () => {
        setCurrentSlide((prev) => (prev - 1 + equipmentImages.length) % equipmentImages.length);
    };

    return (
        <div className="relative max-w-3xl mx-auto">
            {/* Main Image */}
            <div className="relative overflow-hidden rounded-2xl shadow-premium h-[400px] bg-gradient-to-b from-gray-100 to-white">
                {equipmentImages.map((img, index) => (
                    <div
                        key={index}
                        className={`absolute inset-0 transition-opacity duration-500 ${index === currentSlide ? 'opacity-100' : 'opacity-0'
                            }`}
                    >
                        <img
                            src={img.src}
                            alt={img.caption}
                            loading="lazy"
                            decoding="async"
                            width={600}
                            height={400}
                            className="w-full h-full object-contain"
                        />
                        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4">
                            <p className="text-white text-lg font-semibold text-center">
                                {img.caption}
                            </p>
                        </div>
                    </div>
                ))}
            </div>

            {/* Navigation Arrows */}
            <button
                onClick={prevSlide}
                className="absolute left-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
            >
                <ChevronLeft size={24} className="text-coffee-900" />
            </button>
            <button
                onClick={nextSlide}
                className="absolute right-2 top-1/2 -translate-y-1/2 bg-white/90 hover:bg-white p-2 rounded-full shadow-lg transition-all"
            >
                <ChevronRight size={24} className="text-coffee-900" />
            </button>

            {/* Dot Indicators */}
            <div className="flex justify-center gap-2 mt-4">
                {equipmentImages.map((_, index) => (
                    <button
                        key={index}
                        onClick={() => setCurrentSlide(index)}
                        className={`w-3 h-3 rounded-full transition-all ${index === currentSlide
                            ? 'bg-zen-gold w-6'
                            : 'bg-coffee-300 hover:bg-coffee-400'
                            }`}
                    />
                ))}
            </div>
        </div>
    );
};

const Services = () => {
    const [selectedService, setSelectedService] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);

    const [isBookingModalOpen, setIsBookingModalOpen] = useState(false);

    const openModal = (service) => {
        setSelectedService(service);
        setIsModalOpen(true);
    };

    const openBookingModal = (service) => {
        setSelectedService(service);
        setIsBookingModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setIsBookingModalOpen(false);
    };

    const serviceCategories = [
        {
            category: 'Ayurvedic Therapies',
            subtitle: 'Traditional Indian Healing',
            services: [
                {
                    image: serviceAyurvedic,
                    name: 'Ayurvedic Full Body Massage',
                    duration: '60 min',
                    price: 'Rs. 1,800',
                    description: 'Ancient Indian healing massage with warm herbal oils',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A traditional full-body massage using warm herbal oils tailored to your body type (Dosha). This therapy improves circulation, eliminates toxins, and induces deep relaxation. It helps in balancing the body’s energy channels and rejuvenating the skin.',
                        benefits: ['Improves blood circulation', 'Detoxifies the body', 'Reduces stress and anxiety', 'Moisturizes and rejuvenates skin', 'Balances body energy (Doshas)'],
                        process: ['Consultation to determine body type', 'Selection of specific herbal oils', 'Head-to-toe warm oil massage', 'Rhythmic strokes to stimulate energy flow', 'Steam bath (optional/if available)'],
                        recommendedFor: ['Stress relief', 'Fatigue', 'General wellness', 'Dry skin conditions']
                    }
                },
                {
                    image: serviceKatiBasti,
                    name: 'Kati Basti + Full Body Abhyanga',
                    duration: '60 min',
                    price: 'Rs. 2,500',
                    description: 'Specialized lower back treatment with full body massage',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A specialized Ayurvedic treatment for lower back pain. Warm medicated oil is retained over the lower back using a dough ring, followed by a full body Abhyanga massage. This deeply penetrates the muscles to relieve pain and stiffness.',
                        benefits: ['Relieves chronic back pain', 'Strengthens back muscles', 'Lubricates joints', 'Alleviates sciatica symptoms', 'Improves spinal flexibility'],
                        process: ['Dough ring placement on lower back', 'Pouring warm medicated oil', 'Oil retention for specific duration', 'Gentle local massage', 'Full body Ayurvedic massage'],
                        recommendedFor: ['Lower back pain', 'Sciatica', 'Lumbar spondylosis', 'Slipped disc recovery', 'Stiffness']
                    }
                },
                {
                    image: serviceJanuBasti,
                    name: 'Janu Basti + Full Body Abhyanga',
                    duration: '60 min',
                    price: 'Rs. 2,500',
                    description: 'Specialized knee treatment with full body massage',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A targeted therapy for knee pain and stiffness. Warm herbal oil is pooled over the knee joint to nourish and strengthen the joint structure, combined with a relaxing full body massage.',
                        benefits: ['Reduces knee pain', 'Improves joint lubrication', 'Strengthens knee muscles', 'Increases mobility', 'Prevents degenerative changes'],
                        process: ['Dough ring creation around knees', 'Pooling warm herbal oil', 'Temperature maintenance', 'Local massage', 'Full body Abhyanga'],
                        recommendedFor: ['Knee pain', 'Osteoarthritis', 'Stiff joints', 'Athletes', 'Elderly care']
                    }
                },
                {
                    image: serviceGreevaBasti,
                    name: 'Greeva Basti + Full Body Abhyanga',
                    duration: '60 min',
                    price: 'Rs. 2,500',
                    description: 'Specialized neck treatment with full body massage',
                    badge: 'Regular',
                    details: {
                        longDescription: 'An effective Ayurvedic treatment for neck pain and cervical issues. Warm oil is retained on the neck region to relieve tension, followed by a complete body massage for total relaxation.',
                        benefits: ['Relieves neck pain and stiffness', 'Treats cervical spondylosis', 'Reduces shoulder tension', 'Improves neck mobility', 'Alleviates radiating arm pain'],
                        process: ['Dough ring on neck area', 'Warm oil retention', 'Focus on cervical vertebrae', 'Neck and shoulder massage', 'Full body massage'],
                        recommendedFor: ['Neck pain', 'Cervical spondylosis', 'Text neck', 'Desk workers', 'Drivers']
                    }
                },
                {
                    image: serviceShirodhara,
                    name: 'Shirodhara + Full Body Abhyanga',
                    duration: '75 min',
                    price: 'Rs. 3,000',
                    description: 'Traditional oil pouring therapy',
                    badge: 'Premium',
                    details: {
                        longDescription: 'A divine therapy where a continuous stream of warm herbal oil is poured onto the forehead (third eye). Combined with a full body massage, it creates a state of deep relaxation and mental clarity.',
                        benefits: ['Deeply relaxes the nervous system', 'Relieves insomnia and headaches', 'Reduces anxiety and depression', 'Improves concentration', 'Nourishes hair and scalp'],
                        process: ['Full body Abhyanga massage first', 'Lying on back with eyes covered', 'Rhythmic pouring of warm oil on forehead', 'Head massage', 'Rest period'],
                        recommendedFor: ['Insomnia', 'Chronic stress', 'Migraines', 'Hair loss', 'Mental fatigue']
                    }
                },
                {
                    image: servicePotli,
                    name: 'Herbal Potli Massage',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Traditional Indian massage with heated herbal pouches',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A therapeutic massage using heated pouches (Potlis) filled with fresh and dried ayurvedic herbs. The heat opens the pores and allows the herbal goodness to penetrate deep into the muscles.',
                        benefits: ['Relieves muscle pain and stiffness', 'Reduces inflammation', 'Improves circulation', 'Detoxifies the body', 'Relieves arthritis pain'],
                        process: ['Warm oil application', 'Heating of herbal potlis', 'Rhythmic tapping and rubbing with potlis', 'Focus on painful areas', 'Relaxing strokes'],
                        recommendedFor: ['Arthritis', 'Muscle stiffness', 'Back pain', 'Joint pain', 'Cold weather wellness']
                    }
                },
                {
                    image: serviceMarma,
                    name: 'Marma Pain Relief Therapy',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Ayurvedic pressure point therapy for pain management',
                    badge: 'Regular',
                    details: {
                        longDescription: 'An ancient therapy focusing on vital energy points (Marmas) in the body. By stimulating these points, blockages are cleared, and energy flow is restored, providing profound pain relief and healing.',
                        benefits: ['Instant pain relief', 'Clears energy blockages', 'Improves organ function', 'Balances body and mind', 'Enhances flexibility'],
                        process: ['Identification of blocked Marma points', 'Application of specific oils', 'Gentle to deep pressure on points', 'Circular massage movements', 'Energy balancing'],
                        recommendedFor: ['Chronic pain', 'Sports injuries', 'Digestive issues', 'Nervous system disorders']
                    }
                },
                {
                    image: serviceParalysis,
                    name: 'Paralysis Massage',
                    duration: '60 min',
                    price: 'Rs. 1,800',
                    description: 'Specialized therapy for paralyzed patients to improve mobility',
                    badge: 'Specialized',
                    details: {
                        longDescription: 'A specialized, gentle, and repetitive massage therapy designed to stimulate nerves and muscles in stroke or paralysis patients. It helps prevent muscle atrophy and improves blood circulation.',
                        benefits: ['Prevents muscle wasting', 'Improves blood circulation', 'Stimulates nerve endings', 'Maintains joint flexibility', 'Reduces swelling'],
                        process: ['Gentle warm oil application', 'Passive muscle movements', 'Stimulating strokes', 'Joint mobilization exercises', 'Regular sessions recommended'],
                        recommendedFor: ['Stroke recovery', 'Paralysis/Hemiplegia', 'Bedridden patients', 'Muscle weakness']
                    }
                },
                {
                    image: serviceBabyMother,
                    name: 'Baby & Mother Massage',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Gentle postnatal care for mother and baby',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A nurturing service for new mothers and infants. Postnatal massage for moms helps recovery, while gentle baby massage aids in growth, sleep, and digestion.',
                        benefits: ['Postpartum recovery for mom', 'Reduces body aches', 'Improves baby sleep patterns', ' aids baby digestion', 'Strengthens mother-baby bond'],
                        process: ['Gentle massage for mother (back, legs)', 'Soft touches for baby', 'Use of baby-safe oils', 'Focus on relaxation', 'Guidance on handling baby'],
                        recommendedFor: ['New mothers', 'Infants (1 month+)', 'Postnatal recovery', 'Sleep issues in babies']
                    }
                },
                {
                    image: serviceNadiSweda,
                    name: 'Ayurvedic Nadi Sweda (Steam Therapy)',
                    duration: '20 min',
                    price: 'Rs. 750',
                    description: 'Localized steam therapy to relieve pain and stiffness',
                    badge: 'Budget-Friendly',
                    details: {
                        longDescription: 'An ancient Ayurvedic therapy using localized steam to penetrate deep into muscles and joints. The therapeutic steam opens pores, improves circulation, and provides instant relief from pain and stiffness. This targeted treatment is perfect for specific problem areas.',
                        benefits: ['Relieves muscle pain and stiffness', 'Reduces joint inflammation', 'Improves blood circulation', 'Opens skin pores for detoxification', 'Quick and effective relief'],
                        process: ['Identification of problem area', 'Application of herbal oils', 'Localized steam therapy', 'Gentle massage post-steam', 'Cooling down period'],
                        recommendedFor: ['Localized pain', 'Joint stiffness', 'Arthritis', 'Sports injuries', 'Quick relief sessions']
                    }
                },
                {
                    image: serviceNervousSystem,
                    name: 'Nervous System Boost Massage',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Specialized massage targeting nerve pathways for deep rejuvenation',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A highly therapeutic massage designed to stimulate and heal the nervous system. By using specialized techniques along major nerve pathways, this massage helps reduce stress, improve sleep quality, and enhance overall neurological function.',
                        benefits: ['Calms the nervous system', 'Improves sleep quality', 'Reduces physical and mental fatigue', 'Enhances neurological function', 'Boosts immunity'],
                        process: ['Consultation and relaxation breathing', 'Warm oil application', 'Targeted stimulation of nerve pathways', 'Focus on spine and head', 'Rest and energy balancing'],
                        recommendedFor: ['Chronic stress', 'Insomnia', 'Mental fatigue', 'Nervous system disorders']
                    }
                }
            ]
        },
        {
            category: 'Spa Therapies',
            subtitle: 'Relaxation & Rejuvenation',
            services: [
                {
                    image: serviceHeadNeckNew,
                    name: 'Head, Neck & Shoulder',
                    duration: '30 min',
                    price: 'Rs. 850',
                    description: 'Targeted relief for upper body tension and headaches',
                    badge: 'Budget-Friendly',
                    details: {
                        longDescription: 'A focused massage targeting the areas where we hold the most stress. Using pressure point techniques on the head, neck, and shoulders to release tension and melt away headaches.',
                        benefits: ['Relieves tension headaches', 'Reduces neck stiffness', 'Lowers stress levels', 'Improves posture', 'Enhances mental clarity'],
                        process: ['Dry or oil application (client choice)', 'Deep pressure on shoulders', 'Neck stretches', 'Scalp massage', 'Pressure point stimulation'],
                        recommendedFor: ['Desk workers', 'Migraine sufferers', 'Stiff neck', 'Quick stress relief']
                    }
                },
                {
                    image: serviceFootReflex,
                    name: 'Foot Reflexology',
                    duration: '30 min',
                    price: 'Rs. 850',
                    description: 'Pressure point therapy for relaxation and healing',
                    badge: 'Budget-Friendly',
                    details: {
                        longDescription: 'Based on the principle that points on the feet correspond to different organs and systems. By applying pressure to these reflex points, we promote health and relaxation throughout the entire body.',
                        benefits: ['Improves blood circulation', 'Reduces foot pain', 'Promotes better sleep', 'Balances body systems', 'Relieves tired feet'],
                        process: ['Foot cleansing', 'Warm oil application', 'Thumb walking technique on soles', 'Pressure on reflex points', 'Ankle mobilization'],
                        recommendedFor: ['Standing professionals', 'Plantar fasciitis', 'Insomnia', 'Tired legs']
                    }
                },
                {
                    image: serviceThaiNew,
                    name: 'Thai Massage (Dry)',
                    duration: '60 min',
                    price: 'Rs. 1,700',
                    description: 'Traditional Thai stretching and pressure techniques',
                    badge: 'Regular',
                    details: {
                        longDescription: 'Often called "lazy man’s yoga," this dry massage involves rhythmic pressing and stretching of the entire body. It improves flexibility and releases energy blockages without the use of oil.',
                        benefits: ['Increases flexibility', 'Energizes the body', 'Relieves muscle tension', 'Improves range of motion', 'Centers the mind'],
                        process: ['Performed fully clothed on a mat', 'Rhythmic compressions', 'Assisted yoga-like stretches', 'Joint mobilization', 'Energy line work'],
                        recommendedFor: ['Flexibility issues', 'Athletes', 'Low energy', 'Muscle stiffness']
                    }
                },
                {
                    image: serviceSwedish,
                    name: 'Swedish Massage',
                    duration: '60 min',
                    price: 'Rs. 1,700',
                    description: 'Gentle, relaxing full-body massage for stress relief',
                    popular: true,
                    details: {
                        longDescription: 'The classic relaxation massage. It uses long, flowing strokes to ease muscle tension and improve circulation. Perfect for first-timers or anyone looking to simply unwind and de-stress.',
                        benefits: ['Total relaxation', 'Reduces cortisol (stress hormone)', 'Improves circulation', 'Boosts mood', 'Softens skin'],
                        process: ['Long gliding strokes', 'Kneading of muscles', 'Rhythmic tapping', 'Friction techniques', 'Full body integration'],
                        recommendedFor: ['Stress relief', 'First-time massage', 'Relaxation', 'Anxiety reduction']
                    }
                },
                {
                    image: serviceDeepTissueNew,
                    name: 'Deep Tissue Massage',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Intense muscle therapy for athletes and deep tension relief',
                    badge: 'Regular',
                    details: {
                        longDescription: 'Similar to Swedish massage, but with deeper pressure to release chronic muscle tension. It targets the inner layers of your muscles and connective tissues.',
                        benefits: ['Relieves chronic pain', 'Breaks down scar tissue', 'Improves posture', 'Lowers blood pressure', 'Rehabilitates injured muscles'],
                        process: ['Slow, deep strokes', 'Firm pressure', 'Cross-fiber friction', 'Focus on problem areas', 'Use of fingers, thumbs, and knuckles'],
                        recommendedFor: ['Chronic pain', 'Muscle knots', 'Athletes', 'Post-workout recovery']
                    }
                },
                {
                    image: serviceSportsNew,
                    name: 'Sports Therapy',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Specialized treatment for athletic performance and recovery',
                    badge: 'Regular',
                    details: {
                        longDescription: 'Designed for active individuals, this massage focuses on preventing and treating injury, and enhancing athletic performance. It includes stretching and targeted muscle work.',
                        benefits: ['Prevents injuries', 'Enhances athletic performance', 'Faster recovery time', 'Increases flexibility', 'Reduces fatigue'],
                        process: ['Pre-event or post-event focus', 'Stretching techniques', 'Compression', 'Trigger point therapy', 'Muscle flushing'],
                        recommendedFor: ['Runners', 'Gym goers', 'Athletes', 'Active lifestyle']
                    }
                },
                {
                    image: serviceBodyScrub,
                    name: 'Full Body Scrub',
                    duration: '30 min',
                    price: 'Rs. 1,500',
                    description: 'Exfoliating treatment for smooth, rejuvenated skin',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A complete body exfoliation treatment that removes dead skin cells, promotes cell regeneration, and leaves your skin feeling incredibly soft and smooth.',
                        benefits: ['Removes dead skin cells', 'Unclogs pores', 'Improves skin texture', 'Promotes glowing skin', 'Prepared skin for moisturizing'],
                        process: ['Application of exfoliating scrub', 'Circular scrubbing motions', 'Focus on rough areas (elbows, knees)', 'Wiping/Cleansing', 'Moisturizer application'],
                        recommendedFor: ['Dull skin', 'Before tanning', 'Dry skin', 'Special occasions']
                    }
                },
                {
                    image: serviceCoupleNew,
                    name: 'Couple Massage',
                    duration: '120 min',
                    price: 'Rs. 3,500',
                    description: 'Romantic and relaxing massage experience for couples',
                    badge: 'Premium',
                    details: {
                        longDescription: 'Enjoy a relaxing massage side-by-side with your partner. A perfect way to bond and unwind together. You can each choose your preferred style (Swedish or Deep Tissue).',
                        benefits: ['Shared experience', 'Quality time together', 'Reduces stress for both', 'Great for anniversaries', 'Romantic bonding'],
                        process: ['Side-by-side tables', 'Two therapists working simultaneously', 'Choice of oils', 'Relaxing ambiance', 'Synchronized relaxation'],
                        recommendedFor: ['Couples', 'Anniversaries', 'Mother-Daughter', 'Best friends']
                    }
                },
                {
                    image: serviceBodyPoint,
                    name: 'Body Point Relief Massage',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Targeted pressure point therapy for deep relaxation and pain relief',
                    badge: 'Regular',
                    details: {
                        longDescription: 'A specialized massage therapy focusing on specific body points to release tension, alleviate pain, and restore energy balance throughout the body.',
                        benefits: ['Targeted pain relief', 'Releases muscle tension', 'Improves energy flow', 'Promotes deep relaxation', 'Enhances overall well-being'],
                        process: ['Consultation for problem areas', 'Warm oil application', 'Targeted pressure point stimulation', 'Deep tissue techniques', 'Relaxing finishing strokes'],
                        recommendedFor: ['Chronic pain sufferers', 'Stress relief', 'Muscle stiffness', 'General wellness']
                    }
                },
                {
                    image: service10Finger,
                    name: '10 Finger Touch Massage (Happy Moment)',
                    duration: '60 min',
                    price: 'Rs. 2,500',
                    description: 'A deeply relaxing, joyful 10-finger touch massage experience',
                    badge: 'Premium',
                    details: {
                        longDescription: 'Experience pure bliss with our specialized 10-finger touch massage. Designed to release all tension and elevate your mood, this joyful therapy combines smooth, rhythmic strokes using all 10 fingers for an unmatched relaxing sensation.',
                        benefits: ['Elevates mood and happiness', 'Releases deep-seated tension', 'Provides total body relaxation', 'Improves blood circulation', 'Reduces physical stress'],
                        process: ['Warm oil application', 'Synchronized 10-finger strokes', 'Focus on major tension points', 'Rhythmic back massage', 'Relaxing head and neck finish'],
                        recommendedFor: ['Stress relief', 'Mood enhancement', 'Special occasions', 'General relaxation']
                    }
                },
                {
                    image: serviceSleep,
                    name: 'Sleep Therapy Massage',
                    duration: '60 min',
                    price: 'Rs. 2,000',
                    description: 'Gentle, soothing massage aimed at promoting deep, restful sleep',
                    badge: 'Regular',
                    details: {
                        longDescription: 'Struggling with sleep? Our Sleep Therapy Massage uses slow, rhythmic movements and specific pressure points to calm the mind and body, promoting a deep and restorative night’s sleep.',
                        benefits: ['Promotes deep, restful sleep', 'Reduces insomnia and restlessness', 'Calms the nervous system', 'Relieves mental fatigue', 'Lowers anxiety levels'],
                        process: ['Dim ambient lighting setup', 'Application of calming essential oils', 'Slow, soothing strokes', 'Focus on head, neck, and shoulders', 'Guided relaxation breathing'],
                        recommendedFor: ['Insomnia sufferers', 'High stress levels', 'Frequent travelers / jet lag', 'Anxiety relief']
                    }
                },
                {
                    image: serviceDeepTissueNew,
                    name: 'Full Body Pain Relief Massage',
                    duration: '60 min',
                    price: 'Rs. 2,500',
                    description: 'Comprehensive full-body treatment specifically engineered to eliminate pain and chronic aches',
                    badge: 'Specialized',
                    details: {
                        longDescription: 'A highly focused full-body massage therapy designed for those suffering from widespread body pain, chronic aches, or severe muscle stiffness. This comprehensive treatment addresses tension from head to toe to restore your body\'s natural comfort.',
                        benefits: ['Eliminates full-body chronic pain', 'Significantly reduces muscle stiffness', 'Improves overall joint mobility', 'Reduces inflammation', 'Restores physical energy'],
                        process: ['Full-body pain assessment', 'Deep tissue warming techniques', 'Targeted pain relief massage strokes', 'Joint mobilization', 'Soothing cool-down phase'],
                        recommendedFor: ['Chronic generalized pain', 'Severe muscle tension', 'Physical fatigue', 'Extensive body aches']
                    }
                },
                {
                    image: serviceHairRemoval,
                    name: 'Full Body Hair Remover',
                    duration: '30 min',
                    price: 'Rs. 1,500',
                    description: 'Complete body waxing for smooth, hair-free skin',
                    badge: 'Regular',
                    details: {
                        longDescription: 'Professional hair removal service using high-quality wax/cream. Leaves your skin smooth and hair-free for weeks.',
                        benefits: ['Smooth skin', 'Long-lasting results', 'Slower hair regrowth', 'Exfoliates skin', 'hygienic'],
                        process: ['Skin preparation', 'Application of wax/cream', 'Quick removal', 'Soothing oil application', 'Post-wax care advice'],
                        recommendedFor: ['Smooth skin look', 'Hygiene', 'Sports (swimmers/cyclists)', 'Personal preference']
                    }
                },
            ]
        },
    ];

    return (
        <section id="services" className="py-20 pb-32 bg-gradient-to-b from-cream to-white">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Section Header */}
                <div className="text-center mb-16">
                    <div className="inline-flex items-center bg-zen-gold/10 px-6 py-2 rounded-full mb-4">
                        <span className="text-coffee-900 font-semibold">Our Services</span>
                    </div>
                    <h2 className="text-4xl md:text-5xl font-display font-bold text-coffee-900 mb-4">
                        Professional and Personal Massage Therapies
                    </h2>

                    <div className="mt-8 mb-6 max-w-lg mx-auto bg-coffee-50/50 border border-coffee-200/50 rounded-2xl p-6 shadow-sm backdrop-blur-sm">
                        <p className="text-coffee-900 font-bold mb-4 text-center tracking-wide uppercase text-xs">
                            All Services Include:
                        </p>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 text-left max-w-md mx-auto px-4">
                            <div className="flex items-center gap-3 text-coffee-800 bg-white/60 py-2.5 px-4 rounded-xl border border-coffee-100/80 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-white">
                                <span className="text-zen-gold font-bold">✓</span>
                                <span className="font-medium text-sm">Massage Table</span>
                            </div>
                            <div className="flex items-center gap-3 text-coffee-800 bg-white/60 py-2.5 px-4 rounded-xl border border-coffee-100/80 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-white">
                                <span className="text-zen-gold font-bold">✓</span>
                                <span className="font-medium text-sm">3 Types Hot Oil</span>
                            </div>
                            <div className="flex items-center gap-3 text-coffee-800 bg-white/60 py-2.5 px-4 rounded-xl border border-coffee-100/80 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-white">
                                <span className="text-zen-gold font-bold">✓</span>
                                <span className="font-medium text-sm">Disposable Towel</span>
                            </div>
                            <div className="flex items-center gap-3 text-coffee-800 bg-white/60 py-2.5 px-4 rounded-xl border border-coffee-100/80 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-white">
                                <span className="text-zen-gold font-bold">✓</span>
                                <span className="font-medium text-sm">Bed Sheet</span>
                            </div>
                            <div className="flex items-center gap-3 text-coffee-800 bg-white/60 py-2.5 px-4 rounded-xl border border-coffee-100/80 shadow-sm transition-all duration-300 hover:shadow-md hover:bg-white sm:col-span-2">
                                <span className="text-zen-gold font-bold">✓</span>
                                <span className="font-medium text-sm">Full Equipment</span>
                            </div>
                        </div>
                    </div>

                    <p className="text-lg text-red-600 font-semibold mt-2">
                        ⚠ Only Male Therapist Available – No Female Therapist
                    </p>
                </div>

                {/* Featured Image */}
                <div className="mb-16 rounded-2xl overflow-hidden shadow-premium max-w-2xl mx-auto">
                    <img
                        src={massageImg}
                        alt="Professional Massage Therapy"
                        loading="eager"
                        decoding="async"
                        width={800}
                        height={400}
                        className="w-full h-auto max-h-[400px] object-contain bg-gradient-to-b from-gray-50 to-white"
                    />
                </div>

                {/* Service Categories */}
                {serviceCategories.map((category, catIndex) => (
                    <div key={catIndex} className="mb-16">
                        {/* Category Header */}
                        <div className="text-center mb-8">
                            <h3 className="text-3xl font-display font-bold text-coffee-900 mb-2">
                                {category.category}
                            </h3>
                            <p className="text-zen-gold font-semibold">
                                {category.subtitle}
                            </p>
                        </div>

                        {/* Services Grid */}
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                            {category.services.map((service, index) => (
                                <div
                                    key={index}
                                    className="premium-card bg-gradient-to-br from-white to-cream rounded-2xl shadow-premium overflow-hidden flex flex-col h-full"
                                >
                                    {/* Service Image */}
                                    <div className="relative h-40 overflow-hidden">
                                        <img
                                            src={service.image}
                                            alt={service.name}
                                            loading="lazy"
                                            decoding="async"
                                            width={300}
                                            height={160}
                                            className="w-full h-full object-cover transition-transform duration-300 hover:scale-110"
                                        />
                                        {/* Badges */}
                                        {service.popular && (
                                            <span className="absolute top-3 right-3 bg-zen-gold text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                                POPULAR
                                            </span>
                                        )}
                                        {service.badge === 'Budget-Friendly' && (
                                            <span className="absolute top-3 right-3 bg-green-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                                BUDGET-FRIENDLY
                                            </span>
                                        )}
                                        {service.badge === 'Premium' && (
                                            <span className="absolute top-3 right-3 bg-purple-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                                PREMIUM
                                            </span>
                                        )}
                                        {service.badge === 'Specialized' && (
                                            <span className="absolute top-3 right-3 bg-blue-600 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                                SPECIALIZED
                                            </span>
                                        )}
                                        {service.badge === 'Regular' && (
                                            <span className="absolute top-3 right-3 bg-slate-500 text-white px-3 py-1 rounded-full text-xs font-bold shadow-lg">
                                                REGULAR
                                            </span>
                                        )}
                                    </div>

                                    {/* Card Content */}
                                    <div className="p-5 flex flex-col flex-grow">
                                        <h4 className="text-lg font-display font-bold text-coffee-900 mb-2">
                                            {service.name}
                                        </h4>

                                        <div className="flex items-center gap-2 text-coffee-600 mb-2">
                                            <Clock size={14} />
                                            <span className="text-sm">{service.duration}</span>
                                        </div>

                                        <p className="text-coffee-700 text-sm mb-4 line-clamp-2">
                                            {service.description}
                                        </p>

                                        <div className="mt-auto flex items-center justify-between pt-3 border-t border-coffee-900/10 mb-3">
                                            <span className="text-xl font-display font-bold text-zen-gold">
                                                {service.price}
                                            </span>
                                            <span className="text-xs text-coffee-600">per session</span>
                                        </div>

                                        {/* Buttons */}
                                        <div className="flex gap-2">
                                            <button
                                                onClick={() => openModal(service)}
                                                className="flex-1 flex items-center justify-center gap-1 bg-white border border-zen-gold text-zen-gold py-2.5 rounded-xl font-semibold text-sm hover:bg-zen-gold hover:text-white transition-all duration-300"
                                            >
                                                <Info size={16} />
                                                Details
                                            </button>
                                            <button
                                                onClick={() => openBookingModal(service)}
                                                className="flex-1 flex items-center justify-center gap-1 bg-green-500 hover:bg-green-600 text-white py-2.5 rounded-xl font-semibold text-sm transition-all duration-300 shadow-md hover:shadow-lg"
                                            >
                                                <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z" />
                                                </svg>
                                                Book
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                ))}

                {/* Home Service Note with Equipment Carousel */}
                <div className="bg-gradient-to-br from-zen-gold/10 to-zen-gold/5 p-8 rounded-2xl">
                    <h3 className="text-2xl md:text-3xl font-display font-bold text-coffee-900 mb-4 text-center">
                        Complete Home Service Available
                    </h3>
                    <p className="text-coffee-700 mb-8 text-center max-w-2xl mx-auto">
                        We bring everything to your home - Massage Table, 3 Types Hot Oil, Disposable Towel, Bed Sheet, Relax Music & Full Equipment
                    </p>

                    {/* Equipment Carousel */}
                    <EquipmentCarousel />

                    <div className="text-center mt-8">
                        <a
                            href="tel:+919901853969"
                            className="inline-block bg-zen-gold text-white px-8 py-4 rounded-full font-semibold text-lg hover:bg-coffee-900 transition-all duration-300 shadow-premium hover:shadow-glow"
                        >
                            Book Now: +91 9901853969
                        </a>
                    </div>
                </div>
            </div>
            <ServiceDetailModal
                isOpen={isModalOpen}
                onClose={closeModal}
                service={selectedService}
                onBook={openBookingModal}
            />
            <BookingModal
                isOpen={isBookingModalOpen}
                onClose={closeModal}
                service={selectedService}
            />
        </section>
    );
};

export default Services;
